import {
  Col,
  Layout,
  Row,
  RowProps,
  Space,
  Switch,
  theme,
  Typography,
} from "antd";
import { Gutter } from "antd/lib/grid/row";
import React, { useEffect, useState } from "react";
import useSound from "use-sound";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";
import { updateDarkMode } from "../../features/admin/currentAdminSlice";
import { TRIP_STATUS } from "../../shared/constants/TripStatus";
import NotificationPanel from "./NotificationPanel/notificationPanel";

const { Content } = Layout;
const { Title } = Typography;

interface Props extends RowProps {
  title?: string;
  children: React.ReactNode;
  showNotification?: boolean;
  defaultContainer?: boolean;
  gutter?: Gutter | [Gutter, Gutter];
  setShouldSound?: React.Dispatch<React.SetStateAction<boolean>>;
  showSound?: boolean;
}

const ContainerLayout = ({
  title,
  children,
  showNotification = true,
  defaultContainer = true,
  gutter,
  justify,
  showSound,
  ...rest
}: Props): JSX.Element => {
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const dispatch = useAppDispatch();
  const darkMode: boolean = useAppSelector(
    (state: RootState) => state.currentAdmin.value.darkMode,
  );
  const { value: userRequests } = useAppSelector(
    (state: RootState) => state.userRequestList,
  );
  const [shouldSound, setShouldSound] = useState(false);
  const [play, { stop }] = useSound(
    process.env.REACT_APP_SOUND_URL ||
      "https://s3-mowi-bucket.s3.sa-east-1.amazonaws.com/bipping.mp3",
    { loop: true },
  );

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  useEffect(() => {
    if (!shouldSound) {
      stop();
      return;
    }

    const isSearching = userRequests?.some(
      (userRequest) =>
        userRequest.status === TRIP_STATUS.Searching &&
        !userRequest.isAirportTrip,
    );

    if (isSearching) {
      play();
    } else {
      stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSound, userRequests]);

  return (
    <Layout>
      <Row
        align="middle"
        justify="space-between"
        style={{ background: colorBgContainer, padding: "16px" }}
      >
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            {title}
          </Title>
        </Col>
        <Col>
          <Space>
            {showSound && (
              <Space>
                <Typography>Sonido</Typography>
                <Switch
                  value={shouldSound}
                  onChange={() => setShouldSound(!shouldSound)}
                />
              </Space>
            )}
            <Space>
              <Typography>Tema</Typography>
              <Switch
                checkedChildren="Oscuro"
                unCheckedChildren="Claro"
                value={darkMode}
                onChange={() => dispatch(updateDarkMode(!darkMode))}
              />
            </Space>
            {showNotification && <NotificationPanel />}
          </Space>
        </Col>
      </Row>
      <Content>
        {defaultContainer ? (
          <Row
            className="custom-card-container-layout"
            gutter={gutter ?? [32, 16]}
            justify={justify}
            {...rest}
          >
            {children}
          </Row>
        ) : (
          children
        )}
      </Content>
    </Layout>
  );
};

export default ContainerLayout;
